import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Copy, Check, Smartphone, Landmark, Heart, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useDonate } from "../context/DonateContext";
import { TRUST } from "../data/content";

const CopyRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
        <p className="text-sm font-medium text-slate-800 truncate">{value}</p>
      </div>
      <button
        onClick={copy}
        data-testid={`donate-copy-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
        className="shrink-0 p-2 rounded-lg bg-slate-100 hover:bg-emerald-100 text-slate-600 hover:text-emerald-700 transition-colors"
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
};

export const DonateModal = () => {
  const { open, setOpen } = useDonate();
  const [methods, setMethods] = useState([]);

  useEffect(() => {
    if (open) api.get("/donations").then((r) => setMethods(r.data)).catch(() => {});
  }, [open]);

  const upi = methods.find((m) => m.method_type === "upi");
  const banks = methods.filter((m) => m.method_type === "bank");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0" data-testid="donate-modal-container">
        <div className="bg-[#0F2942] px-6 py-5 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-xl text-white">
              <Heart className="h-5 w-5 text-amber-400 fill-amber-400" /> Support Our Mission
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-slate-300 mt-1">
            Your contribution funds education, health camps and social welfare in rural Bihar.
          </DialogDescription>
        </div>

        <div className="p-6">
          <Tabs defaultValue="upi">
            <TabsList className="grid grid-cols-2 w-full mb-4">
              <TabsTrigger value="upi" data-testid="donate-tab-upi" className="gap-1.5">
                <Smartphone className="h-4 w-4" /> UPI
              </TabsTrigger>
              <TabsTrigger value="bank" data-testid="donate-tab-bank" className="gap-1.5">
                <Landmark className="h-4 w-4" /> Bank Transfer
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upi">
              {upi ? (
                <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/60">
                  <CopyRow label="UPI ID" value={upi.upi_id} />
                  <CopyRow label="Payee Name" value={upi.display_name || TRUST.name} />
                  <p className="text-xs text-slate-500 mt-3">
                    Open any UPI app (PhonePe, Google Pay, Paytm) and pay to the above UPI ID.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-slate-500">UPI details will be updated shortly.</p>
              )}
            </TabsContent>

            <TabsContent value="bank" className="space-y-4">
              {banks.length ? (
                banks.map((b) => (
                  <div key={b.id} className="rounded-xl border border-slate-200 p-4 bg-slate-50/60">
                    <CopyRow label="Bank Name" value={b.bank_name} />
                    <CopyRow label="Account Holder" value={b.account_holder} />
                    <CopyRow label="Account Number" value={b.account_number} />
                    <CopyRow label="IFSC Code" value={b.ifsc} />
                    <CopyRow label="Branch" value={b.branch} />
                    <CopyRow label="Account Type" value={b.account_type} />
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">Bank details will be updated shortly.</p>
              )}
            </TabsContent>
          </Tabs>

          <div className="mt-4 flex items-start gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg p-3">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
            <span>100% of donations go to our charitable programmes. PAN: {TRUST.pan} · {TRUST.deedNo}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DonateModal;
